import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch, useSelector } from 'react-redux';
import { setLoader, setErrorAlert } from '../../store/alert/alertSlice';
import { getConnectionStatus } from '../../utils/connectors/connectorsService';
import { ReactComponent as BandsintownLogo } from '../../assets/images/components/bandsintown-svgrepo-com.svg';

const ConnectorsApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bandsintownSettings = useSelector(state => state.bandsintown.settings);
  
  // Initialize connector statuses from localStorage to avoid loading state
  const [connectorStatuses, setConnectorStatuses] = useState(() => {
    try {
      const savedStatuses = localStorage.getItem('connectorStatuses');
      if (savedStatuses) {
        return JSON.parse(savedStatuses);
      }
    } catch (error) {
      console.error('Error loading saved connector statuses:', error);
    }
    return {
      google: false,
      bandsintown: false
    };
  });
  const [loading, setLoading] = useState(false); // Don't show loading by default

  // Available connectors - easily extensible for future integrations
  const connectors = [
    {
      id: 'google',
      name: 'Google',
      color: '#4285F4',
      description: 'Calendar sync, email tracking, and AI analysis',
      route: '/connectors/google',
      enabled: true
    },
    {
      id: 'bandsintown',
      name: 'Bandsintown',
      color: '#00CEC8',
      description: 'Sync tour dates automatically',
      route: '/connectors/bandsintown',
      enabled: true,
      comingSoon: false,
    },
  ];

  const loadConnectorStatuses = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
        dispatch(setLoader(true));
      }
      
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Check connection status for each enabled connector
      const statuses = {
        google: false,
        bandsintown: false
      };
      
      // Google integration status check (consolidated Calendar + Gmail)
      try {
        // Check using 'googleCalendar' for now (backend may still use this identifier)
        // Once backend is updated to use 'google', this can be changed
        const googleStatus = await getConnectionStatus('googleCalendar', token);
        statuses.google = googleStatus.connected;
      } catch (error) {
        console.error('Error checking Google integration status:', error);
        statuses.google = false;
      }

      // Bandsintown status check - use Redux state as primary source
      try {
        if (bandsintownSettings && bandsintownSettings.apiKey && bandsintownSettings.artistName) {
          statuses.bandsintown = true;
        } else {
          // Fallback to localStorage if Redux state is not available
          const localBandsintownSettings = localStorage.getItem('bandsintownSettings');
          if (localBandsintownSettings) {
            const settings = JSON.parse(localBandsintownSettings);
            statuses.bandsintown = !!(settings.apiKey && settings.artistName);
          } else {
            statuses.bandsintown = false;
          }
        }
      } catch (error) {
        console.error('Error checking Bandsintown status:', error);
        statuses.bandsintown = false;
      }
      
      setConnectorStatuses(statuses);
      
      // Save to localStorage for persistence
      try {
        localStorage.setItem('connectorStatuses', JSON.stringify(statuses));
      } catch (error) {
        console.error('Error saving connector statuses to localStorage:', error);
      }
      
    } catch (error) {
      console.error('Error loading connector statuses:', error);
      dispatch(setErrorAlert('Failed to load connector statuses'));
    } finally {
      if (showLoading) {
        setLoading(false);
        dispatch(setLoader(false));
      }
    }
  }, [bandsintownSettings, dispatch]); // Remove connectorStatuses from dependencies

  useEffect(() => {
    // Load statuses silently on initial mount since we have localStorage data
    loadConnectorStatuses(false);
  }, [loadConnectorStatuses]);

  // Direct update for Bandsintown status when settings change
  useEffect(() => {
    if (bandsintownSettings) {
      const isConnected = !!(bandsintownSettings.apiKey && bandsintownSettings.artistName);
      setConnectorStatuses(prev => {
        const newStatuses = {
          ...prev,
          bandsintown: isConnected
        };
        
        // Save to localStorage immediately
        try {
          localStorage.setItem('connectorStatuses', JSON.stringify(newStatuses));
        } catch (error) {
          console.error('Error saving connector statuses to localStorage:', error);
        }
        
        return newStatuses;
      });
    }
  }, [bandsintownSettings]); // Remove connectorStatuses to avoid infinite loop

  const handleConnectorClick = (connector) => {
    if (!connector.enabled) {
      return; // Coming soon connectors are not clickable
    }
    navigate(connector.route);
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Connectors</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate(-1)}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Description */}
            <div className="mb-8">
              <p className="text-white text-[12px] opacity-70 leading-relaxed">
                Connect your favorite platforms to Dizee. Sync your calendar, track analytics, 
                and manage everything from one place.
              </p>
            </div>

            {/* Connectors Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    Loading connectors
                    <span className="loading-dots" style={{
                      marginLeft: '2px'
                    }}></span>
                  </div>
                  <style>{`
                    @keyframes thinking {
                      0%, 20% { content: ''; }
                      40% { content: '.'; }
                      60% { content: '..'; }
                      80%, 100% { content: '...'; }
                    }
                    .loading-dots::after {
                      content: '';
                      animation: thinking 1.2s infinite;
                    }
                  `}</style>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {connectors.map((connector) => {
                  const isConnected = connectorStatuses[connector.id];
                  const isDisabled = !connector.enabled;

                  return (
                    <div
                      key={connector.id}
                      onClick={() => handleConnectorClick(connector)}
                      className={`
                        w-full bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4
                        transition-all relative overflow-hidden
                        ${!isDisabled ? 'hover:border-[rgba(255,255,255,0.3)] cursor-pointer' : 'opacity-50 cursor-not-allowed'}
                      `}
                    >
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {/* Icon */}
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.1)]"
                            style={{ backgroundColor: connector.enabled ? `${connector.color}20` : 'rgba(255,255,255,0.05)' }}
                          >
                            {connector.enabled && (
                              <>
                                {connector.id === 'google' ? (
                                  <svg className="w-6 h-6" viewBox="0 0 488 512" fill={connector.color}>
                                    <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h240z"/>
                                  </svg>
                                ) : connector.id === 'bandsintown' ? (
                                  <BandsintownLogo className="w-6 h-6" style={{ fill: connector.color }} />
                                ) : (
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: connector.color }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-white text-[12px] font-medium">
                                {connector.name}
                              </p>
                            </div>
                            <p className="text-white text-[11px] opacity-50">
                              {connector.description}
                            </p>
                          </div>
                        </div>

                        {/* Status Badge */}
                        {connector.enabled && (
                          <div className="ml-3">
                            {isConnected ? (
                              <div className="flex items-center gap-1.5 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-30 rounded-full px-3 py-1.5">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                <span className="text-green-500 text-[10px] font-medium">
                                  Connected
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-full px-3 py-1.5">
                                <div className="w-2 h-2 bg-white opacity-30 rounded-full" />
                                <span className="text-white text-[10px] opacity-50">
                                  Not Connected
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default ConnectorsApp;

